import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
} from '@mui/material';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import PaletteRounded from '@mui/icons-material/PaletteRounded';
import PhotoCameraRounded from '@mui/icons-material/PhotoCameraRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAppThemeContext, useAuthContext, useDrawerContext } from '../../contexts';
import { Environment } from '../../environment';
import { THEME_OPTIONS, radii } from '../../themes';
import { DialogPefilFoto } from './DialogPefilFoto';
import { DialogPefil } from './DialogPerfil';
import { UsuarioService } from '../../services/api/cadastros/UsuariosService';
import { layoutTokens, SidebarItem, SubmenuPanel } from '../app-shell';

const SIDEBAR_STORAGE_KEY = 'APP_SIDEBAR_EXPANDED';

// Usado por telas de loading em tela cheia para deixar o rail do menu de fora da área
// coberta (ver TelaCarregando.tsx) — corresponde à largura mínima da sidebar (modo recolhido).
export const MENU_LATERAL_RAIL_WIDTH = layoutTokens.sidebarClosed;

const getBaseRoute = (path: string) => {
  const parts = path.split('/').filter(Boolean);
  return parts[0] || '';
};

const ElegantDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'menucolor' && prop !== 'menuborder' && prop !== 'railwidth',
})<{ menucolor: string; menuborder: string; railwidth: number }>(({ menucolor, menuborder, railwidth, theme }) => ({
  width: railwidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: railwidth,
    overflowX: 'hidden',
    backgroundColor: menucolor,
    backgroundImage: 'none',
    border: 'none',
    borderRight: `1px solid ${menuborder}`,
    // Só arredonda o lado visível (direito) — esquerdo/topo/rodapé ficam
    // colados na borda da tela, então não têm canto pra arredondar.
    borderTopRightRadius: radii.xl,
    borderBottomRightRadius: radii.xl,
    transition: theme.transitions.create('width', { duration: 200 }),
  },
}));

interface IMenuLateralProps {
  children: React.ReactNode;
}

export const MenuLateral: React.FC<IMenuLateralProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  // Telas baixas (janela pouco alta, mesmo que larga) não são cobertas pelos breakpoints
  // de largura acima — sem isso a sidebar (logo + avatar + módulos + rodapé) estoura a altura
  // da viewport e força scroll interno na lista de módulos. Existem sempre 5 módulos (ver
  // drawerOptions em routes/index.tsx), por isso os tamanhos abaixo foram calculados para os
  // 5 caberem sem scroll até telas bem baixas (~360px de altura).
  const shortH = useMediaQuery('(max-height:760px)');
  const veryShortH = useMediaQuery('(max-height:620px)');
  const ultraShortH = useMediaQuery('(max-height:500px)');

  const avatarSize = ultraShortH ? 36 : veryShortH ? 44 : shortH ? 56 : 56;
  const itemHeight = ultraShortH ? 36 : veryShortH ? 40 : shortH ? 44 : 48;
  const itemIconSize = ultraShortH ? '1.15rem' : veryShortH ? '1.25rem' : '1.4rem';
  const modulesGap = ultraShortH ? 0.3 : veryShortH ? 0.5 : 0.75;

  const [openModule, setOpenModule] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElTema, setAnchorElTema] = useState<null | HTMLElement>(null);
  const [openFoto, setOpenFoto] = useState(false);
  const [openPerfil, setOpenPerfil] = useState(false);
  const [fotoPerfil, setFotoPerfil] = useState('');
  const [idUser, setIdUser] = useState('');
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [loginUsuario, setLoginUsuario] = useState('');
  const openMenu = Boolean(anchorEl);
  const openMenuTema = Boolean(anchorElTema);

  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarExpanded));
  }, [sidebarExpanded]);

  const { isDrawerOpen, toggleDrawerOpen, drawerOptions, drawerItemOptions } = useDrawerContext();
  const { themeName, setThemeName } = useAppThemeContext();
  const { logout } = useAuthContext();

  // No mobile a sidebar continua sempre em modo rail (ícone + tooltip): é a mesma largura
  // estreita que já existia antes, agora sobre um Drawer temporário — abrir/recolher fica
  // reservado ao desktop, onde há espaço horizontal de sobra para os rótulos.
  const expanded = !smDown && sidebarExpanded;
  const railWidth = smDown ? layoutTokens.sidebarClosed : (sidebarExpanded ? layoutTokens.sidebarOpen : layoutTokens.sidebarClosed);

  // Nome no cartão da sidebar: só os dois primeiros nomes (ex.: "Eduardo
  // Santos" em vez de "Eduardo Santos De Souza") — cabe folgado em 1-2
  // linhas sem precisar decidir se o nome completo cabe ou não.
  const nomeSidebarExibido = nomeUsuario.trim().split(/\s+/).slice(0, 2).join(' ');

  // Sidebar fica escura (navy) nos temas 'light' (padrão) e 'dark' — só
  // fica clara no 'lightWhiteMenu'. Usado pra escolher a versão certa da
  // logo TORQ (branca ou preta) no rodapé.
  const sidebarEscura = themeName !== 'lightWhiteMenu';

  const handleToggleSidebar = () => {
    if (smDown) {
      toggleDrawerOpen();
    } else {
      setSidebarExpanded((old) => !old);
    }
  };

  interface JwtPayload {
    iduser: string;
    img: string;
  }

  useEffect(() => {
    const token = localStorage.getItem('APP_ACCESS_TOKEN');
    if (token) {
      const decoded = jwtDecode<JwtPayload>(token);
      setFotoPerfil(Environment.URL_IMGUSUARIOS + decoded.img);
      setIdUser(decoded.iduser);
    }
  }, []);

  useEffect(() => {
    setOpenModule(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!idUser) return;
    UsuarioService.getByIdPerfil(idUser).then((result) => {
      if (!(result instanceof Error)) {
        setNomeUsuario(result.nome);
        setLoginUsuario(result.login);
      }
    });
  }, [idUser]);

  const currentBase = getBaseRoute(location.pathname);

  const isModuleActive = (modulePath: string) => {
    if (getBaseRoute(modulePath) === currentBase) return true;
    return drawerItemOptions.some(
      (item) => item.parent === modulePath && getBaseRoute(item.path) === currentBase
    );
  };

  const handleModuleClick = (modulePath: string, hasSubItems: boolean) => {
    if (hasSubItems) {
      setOpenModule((old) => (old === modulePath ? null : modulePath));
    } else {
      navigate(modulePath);
      setOpenModule(null);
      if (smDown) toggleDrawerOpen();
    }
  };

  const handleNavigateSubItem = (path: string) => {
    navigate(path);
    setOpenModule(null);
    if (smDown) toggleDrawerOpen();
  };

  const activeModuleOption = drawerOptions.find((d) => d.path === openModule);
  const activeSubItems = useMemo(
    () =>
      drawerItemOptions
        .filter((i) => i.parent === openModule)
        .map((sub) => ({
          icon: sub.icon,
          label: sub.label,
          descricao: sub.descricao,
          path: sub.path,
          active: getBaseRoute(location.pathname) === getBaseRoute(sub.path),
        })),
    [drawerItemOptions, openModule, location.pathname]
  );

  return (
    <>
      <ClickAwayListener
        onClickAway={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest?.('[data-rail-button]')) return;
          setOpenModule(null);
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <ElegantDrawer
            open={isDrawerOpen}
            variant={smDown ? 'temporary' : 'permanent'}
            onClose={toggleDrawerOpen}
            menucolor={theme.torq.sidebar.bg}
            menuborder={theme.torq.sidebar.border}
            railwidth={railWidth}
          >
            <Box height="100%" display="flex" flexDirection="column">
              <Box
                sx={{
                  height: layoutTokens.logoHeaderHeight,
                  minHeight: layoutTokens.logoHeaderHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: expanded ? 'space-between' : 'center',
                  gap: 1.25,
                  px: expanded ? 2.5 : 0,
                  backgroundColor: theme.torq.sidebar.logoAreaBg,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1.25}
                  minWidth={0}
                  onClick={!smDown ? handleToggleSidebar : undefined}
                  sx={{ cursor: smDown ? 'default' : 'pointer' }}
                >
                  {expanded ? (
                    // Wordmark completa (glifo + "SIAC" já desenhados juntos)
                    // — troca de tom conforme a sidebar for escura ou clara,
                    // mesmo critério da logo TORQ do rodapé.
                    <Box
                      component="img"
                      src={sidebarEscura ? '/img/siac-wordmark-white.png' : '/img/siac-wordmark-black.png'}
                      alt="SIAC"
                      sx={{ height: 38, width: 'auto', flexShrink: 0 }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src="/img/siac-logo.png"
                      alt="SIAC"
                      sx={{ width: 30, height: 30, borderRadius: '6px', flexShrink: 0 }}
                    />
                  )}
                </Box>

                {!smDown && expanded && (
                  <IconButton size="small" onClick={handleToggleSidebar} sx={{ color: theme.torq.sidebar.textSecondary, flexShrink: 0 }}>
                    <ChevronLeftRounded fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ pt: 0.75, pb: 1.5, px: expanded ? 1.25 : 1, width: '100%' }}>
                {/* Cartão horizontal (avatar + nome/login) no modo expandido —
                    no modo rail (ícone só) fica só o avatar centralizado,
                    sem cartão, não tem espaço pro texto. */}
                <Box
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: expanded ? 'flex-start' : 'center',
                    gap: 1.1,
                    cursor: 'pointer',
                    ...(expanded && !veryShortH
                      ? {
                        p: 1,
                        borderRadius: `${radii.md}px`,
                        bgcolor: theme.torq.sidebar.hover,
                        border: `1px solid ${theme.torq.sidebar.border}`,
                        transition: 'border-color 0.15s ease',
                        '&:hover': { borderColor: theme.torq.sidebar.activeAccentBar },
                      }
                      : {}),
                  }}
                >
                  <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                    <Avatar
                      src={fotoPerfil}
                      sx={{
                        width: avatarSize,
                        height: avatarSize,
                        // Borda usava um ciano fixo (pensado só pra sidebar
                        // escura) — trocado pelo token de borda do tema, que
                        // já se adapta às três variantes (escuro, claro/menu
                        // escuro, claro/menu claro). Sombra suave dá um
                        // pouco de profundidade ao avatar em qualquer uma
                        // delas.
                        border: `2px solid ${theme.torq.sidebar.border}`,
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.18)',
                      }}
                    />
                    {/* Indicador de status online (spec do menu lateral) — círculo
                        recortado da própria cor de fundo da sidebar, pra parecer
                        "vazado" em vez de sobreposto. */}
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 2,
                        bottom: 2,
                        width: avatarSize <= 36 ? 8 : 10,
                        height: avatarSize <= 36 ? 8 : 10,
                        borderRadius: '50%',
                        bgcolor: '#22C55E',
                        border: `2px solid ${theme.torq.sidebar.bg}`,
                      }}
                    />
                  </Box>

                  {expanded && !veryShortH && (
                    <Box sx={{ minWidth: 0 }}>
                      <Tooltip title={nomeUsuario || loginUsuario}>
                        <Typography
                          fontWeight={700}
                          sx={{
                            lineHeight: 1.2,
                            fontSize: '0.8rem',
                            color: theme.torq.sidebar.textPrimary,
                            wordBreak: 'break-word',
                          }}
                        >
                          {nomeSidebarExibido || 'Usuário'}
                        </Typography>
                      </Tooltip>
                      {!!loginUsuario && (
                        <Typography
                          noWrap
                          sx={{ mt: 0.25, fontSize: '0.68rem', color: theme.torq.sidebar.textSecondary }}
                        >
                          {loginUsuario}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                slotProps={{ paper: { sx: { mt: 1, minWidth: 220, borderRadius: `${radii.lg}px` } } }}
              >
                {/* Cabeçalho com foto grande + nome completo (sem quebra
                    forçada — aqui, diferente do cartão da sidebar, sobra
                    largura de sobra pro nome) + login. */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, pt: 1.5, pb: 1.5 }}>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Avatar
                      src={fotoPerfil}
                      sx={{
                        width: 84,
                        height: 84,
                        border: `3px solid ${theme.torq.sidebar.activeAccentBar}`,
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 4,
                        bottom: 4,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: '#22C55E',
                        border: `2px solid ${theme.palette.background.paper}`,
                      }}
                    />
                  </Box>
                  <Typography fontWeight={700} align="center" sx={{ mt: 1.5, fontSize: '0.95rem', color: theme.palette.text.primary }}>
                    {nomeUsuario || 'Usuário'}
                  </Typography>
                  {!!loginUsuario && (
                    <Typography align="center" sx={{ fontSize: '0.78rem', fontWeight: 600, color: theme.torq.sidebar.activeAccentBar, mt: 0.25 }}>
                      {loginUsuario}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ mb: 0.5 }} />

                <MenuItem
                  onClick={() => {
                    setOpenFoto(true);
                    setAnchorEl(null);
                  }}
                >
                  <PhotoCameraRounded fontSize="small" sx={{ mr: 1 }} />
                  Mudar foto
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setOpenPerfil(true);
                    setAnchorEl(null);
                  }}
                >
                  <LockRounded fontSize="small" sx={{ mr: 1 }} />
                  Alterar senha
                </MenuItem>
              </Menu>

              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                gap={modulesGap}
                sx={{
                  py: 1,
                  // Sem padding à esquerda no modo expandido: o marcador do
                  // item ativo precisa encostar na borda esquerda da sidebar,
                  // sem espaço sobrando entre os dois (o respiro do ícone/
                  // rótulo já vem do padding interno do próprio SidebarItem).
                  pl: expanded ? 0 : 1,
                  pr: expanded ? 1.25 : 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  width: '100%',
                  minHeight: 0,
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.torq.sidebar.scrollbarThumb,
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                }}
              >
                {drawerOptions.map((drawer) => {
                  const hasSubItems = drawerItemOptions.some((i) => i.parent === drawer.path);
                  const active = isModuleActive(drawer.path) || openModule === drawer.path;

                  return (
                    <SidebarItem
                      key={drawer.path}
                      railButton
                      icon={drawer.icon}
                      label={drawer.label}
                      active={active}
                      expanded={expanded}
                      hasChevron={hasSubItems}
                      height={itemHeight}
                      iconSize={itemIconSize}
                      onClick={() => handleModuleClick(drawer.path, hasSubItems)}
                    />
                  );
                })}
              </Box>

              <Divider sx={{ width: '80%', mx: 'auto', mb: 1, borderColor: theme.torq.sidebar.divider }} />

              <Box
                display="flex"
                flexDirection="column"
                gap={modulesGap}
                sx={{ pb: 2, pl: expanded ? 0 : 1, pr: expanded ? 1.25 : 1 }}
              >
                <SidebarItem
                  icon="settings"
                  label="Configurações"
                  expanded={expanded}
                  height={itemHeight}
                  iconSize={itemIconSize}
                  active={getBaseRoute(location.pathname) === 'empresa'}
                  onClick={() => {
                    navigate('/empresa');
                    setOpenModule(null);
                    if (smDown) toggleDrawerOpen();
                  }}
                />

                <SidebarItem
                  icon={<PaletteRounded />}
                  label="Tema"
                  expanded={expanded}
                  height={itemHeight}
                  iconSize={itemIconSize}
                  onClick={(e) => setAnchorElTema(e.currentTarget)}
                />

                <Menu anchorEl={anchorElTema} open={openMenuTema} onClose={() => setAnchorElTema(null)}>
                  {THEME_OPTIONS.map((opt) => (
                    <MenuItem
                      key={opt.name}
                      selected={themeName === opt.name}
                      onClick={() => {
                        setThemeName(opt.name);
                        setAnchorElTema(null);
                      }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: opt.swatch,
                          mr: 1.5,
                          border: '1px solid rgba(0,0,0,0.15)',
                        }}
                      />
                      {opt.label}
                    </MenuItem>
                  ))}
                </Menu>

                <SidebarItem
                  icon="exit_to_app"
                  label="Sair do sistema"
                  expanded={expanded}
                  height={itemHeight}
                  iconSize={itemIconSize}
                  onClick={logout}
                />

                {expanded && !veryShortH && (
                  // Selinho pequeno (cartão arredondado com gradiente sutil),
                  // igual ao espírito da referência, mas bem reduzido pra não
                  // competir com o resto do rodapé.
                  <Box
                    sx={{
                      mt: 0.75,
                      py: 0.5,
                      textAlign: 'center',
                      borderRadius: radii.md,
                      background: `linear-gradient(135deg, ${theme.torq.sidebar.hover}, transparent)`,
                      border: `1px solid ${theme.torq.sidebar.border}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.torq.sidebar.textSecondary, fontSize: '0.6rem', opacity: 0.8 }}
                      >
                        v2.5
                      </Typography>
                      <Box sx={{ width: '1px', height: 14, bgcolor: theme.torq.sidebar.border }} />
                      <Typography
                        variant="caption"
                        sx={{ color: theme.torq.sidebar.activeAccentBar, fontWeight: 700, letterSpacing: '0.4px', fontSize: '0.75rem' }}
                      >
                        ERP
                      </Typography>
                      <Box sx={{ width: '1px', height: 14, bgcolor: theme.torq.sidebar.border }} />
                      <Box
                        component="img"
                        src={sidebarEscura ? '/img/torq-wordmark-white.png' : '/img/torq-wordmark-black.png'}
                        alt="TORQ"
                        sx={{ height: 22, width: 'auto' }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </ElegantDrawer>

          <SubmenuPanel
            open={Boolean(openModule) && !!activeModuleOption}
            left={railWidth}
            title={activeModuleOption?.label}
            items={activeSubItems}
            onItemClick={handleNavigateSubItem}
          />
        </Box>
      </ClickAwayListener>

      {/* Sem a TopBar global, o mobile perde o único abridor do Drawer temporário
          (variant="temporary" fica 100% oculto até isDrawerOpen=true) — este botão
          fixo substitui esse papel só em telas estreitas. */}
      {smDown && (
        <IconButton
          onClick={toggleDrawerOpen}
          sx={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: theme.zIndex.drawer + 1,
            bgcolor: theme.torq.surface.card,
            border: `1px solid ${theme.torq.border.default}`,
            boxShadow: theme.torq.dialogShadow,
            '&:hover': { bgcolor: theme.torq.surface.elevated },
          }}
          size="small"
        >
          <MenuRounded fontSize="small" />
        </IconButton>
      )}

      <Box
        sx={{
          marginLeft: smDown ? 0 : `${railWidth}px`,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          transition: 'margin-left 200ms ease',
          // Contém qualquer conteúdo largo demais (ex.: tabela com muitas
          // colunas) dentro da própria área de conteúdo — sem isso, um
          // descendente mais largo que a tela empurra a página inteira e cria
          // um scroll horizontal duplicado (o da página + o do componente
          // largo). Componentes que realmente precisam rolar na horizontal
          // (TableContainer, por ex.) continuam rolando normalmente por
          // dentro, isso só evita o vazamento pro body.
          overflowX: 'hidden',
        }}
      >
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: 'background.default',
          }}
        >
          {children}
        </Box>
      </Box>

      <DialogPefilFoto
        id={idUser}
        img={fotoPerfil}
        value=""
        open={openFoto}
        onClose={() => setOpenFoto(false)}
      />

      <DialogPefil
        id={idUser}
        value=""
        open={openPerfil}
        onClose={() => setOpenPerfil(false)}
      />
    </>
  );
};
